import { useCallback, useEffect, useMemo } from "react";
import type { MouseEvent } from "react";
import { ItemPreviewHoveringState } from "@/components/item-preview";
import { useHeadsUpContext } from "@/components/heads-up/context/heads-up-context";
import { getHeadsUpSidebarAnchorRect } from "./get-heads-up-sidebar-anchor";
import type { SmartNotePreviewInfo } from "./smart-note-preview.types";
import type { Maybe } from "@/domains/common/types";
import type { INoteObservable } from "@/store/note/types";

type AnchorResolverArgs = {
  event: MouseEvent<HTMLElement>;
};

type AnchorResolver = (args: AnchorResolverArgs) => HTMLElement | null;

export interface HeadsUpPreviewHoverArgs {
  noteId?: string;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
  onPreviewOpen?: () => void;
  resolveAnchor?: AnchorResolver;
}

export interface HeadsUpPreviewHoverHandlers {
  hoverState: ItemPreviewHoveringState;
  handleMouseEnter: (event: MouseEvent<HTMLElement>) => void;
  handleMouseLeave: () => void;
}

export function useHeadsUpPreviewHover(args: HeadsUpPreviewHoverArgs): HeadsUpPreviewHoverHandlers {
  const { noteId, notePreviewInfo, onPreviewOpen, resolveAnchor } = args;
  const hoverState = useMemo(() => new ItemPreviewHoveringState(), []);
  const {
    resources: { loadNote },
  } = useHeadsUpContext();

  useEffect(() => {
    hoverState.onHoverActivate = onPreviewOpen ?? undefined;
    return () => {
      hoverState.onHoverActivate = undefined;
    };
  }, [hoverState, onPreviewOpen]);

  const handleMouseEnter = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (noteId) {
        const hydrationPromise = loadNote(noteId);

        if (notePreviewInfo && !notePreviewInfo.isNoteContentDocumentLoaded) {
          void notePreviewInfo.fetchNoteContentDocument();
        }

        if (hydrationPromise) {
          hydrationPromise
            .then((loaded: Maybe<INoteObservable>) => {
              if (loaded && !loaded.isNoteContentDocumentLoaded) {
                void loaded.fetchNoteContentDocument();
              }
            })
            .catch(() => {});
        }
      }

      const anchorElement =
        resolveAnchor?.({ event }) ?? (event.currentTarget as HTMLElement | null);
      hoverState.hoveredRect = anchorElement ? getHeadsUpSidebarAnchorRect(anchorElement) : null;

      hoverState.handleMouseEnter();
    },
    [hoverState, loadNote, noteId, notePreviewInfo, resolveAnchor]
  );

  const handleMouseLeave = useCallback(() => {
    hoverState.hoveredRect = null;
    hoverState.handleMouseLeave();
  }, [hoverState]);

  return { hoverState, handleMouseEnter, handleMouseLeave };
}
